import { buildDefaultMatrixConfig, type RaceKey } from '@/components/views/Matrix/classes/IMatrixConfig';
import type IMatrixConfig from '@/components/views/Matrix/classes/IMatrixConfig';
import type { CategoryId, GroupId, RaceContestantId, RaceId, StationRoleId } from '@/utilities/typealiases';
import type { MatrixSettings } from './MatrixSettings';
import type { RacesGroupsCategoriesMap } from '@/utilities/RacesGroupsCategoriesMap';
import type { StationRole } from '@/objects/generated';
import {
    buildStationRoleColumnLayoutForRaces,
    buildRaceKeyFromRaceIds,
    getStationRoleCellIdentifierForStationRoleAndLap,
} from '@/components/views/Matrix/classes/MatrixHelper';
import { arrayUnique } from '@/utilities';
import type { MatrixContestant } from '@/components/views/Matrix/classes/MatrixContestant';

type StationRoles = { id: string; enabled: boolean }[];
type RaceGroupIDs = { id: GroupId; enabled: boolean }[];
type CategoryIDs = { id: number; enabled: boolean }[];

type StationRolesMap = Map<RaceKey, StationRoles>;
type RaceGroupMap = Map<RaceKey, RaceGroupIDs>;
type CategoryMap = Map<RaceKey, CategoryIDs>;

/**
 * Version 3 of matrix settings.
 * This has changed the RaceId to a RaceKey compared to V2, so we can have more than one race at the same time in the matrix
 * The StationRoles are now also strings instead of stationIds. The string is the columnId of the column in the grid. Its created by the matrixHelper.
 */
export class SavedMatrixSettingsV3 {
    static NAME_OF_CLASS = 'SavedMatrixSettingsV3';
    public MatrixConfig: IMatrixConfig;

    public StationRolesMap: StationRolesMap;
    public RaceGroupMap: RaceGroupMap;
    public CategoryMap: CategoryMap;

    public Version = 3;

    /**
     * Builds a SavedMatrixSettingsV3 object from a MatrixSettings object. This is used when we want to save the settings to the db.
     * @param matrixSettings The current MatrixSettings
     * @param racesGroupsCategoriesMap The raceGroupCategories map for the event as it currently is
     * @param stationRoles All the stationRoles for the event
     * @returns A SavedMatrixSettingsV3 object
     */
    public static FromMatrixSettings(
        matrixSettings: MatrixSettings,
        racesGroupsCategoriesMap: RacesGroupsCategoriesMap,
        stationRoles: Map<StationRoleId, StationRole>,
        matrixContestants: Map<RaceContestantId, MatrixContestant>,
    ): SavedMatrixSettingsV3 {
        const savedSettings = new SavedMatrixSettingsV3();

        savedSettings.MatrixConfig = matrixSettings.MatrixConfig;

        savedSettings.StationRolesMap = new Map<RaceKey, StationRoles>();
        savedSettings.RaceGroupMap = new Map<RaceKey, RaceGroupIDs>();
        savedSettings.CategoryMap = new Map<RaceKey, CategoryIDs>();

        const races = [
            // All the real races
            ...racesGroupsCategoriesMap.GetRaceIDs().map((raceId) => ({ raceIds: [raceId], raceKey: buildRaceKeyFromRaceIds([raceId]) })),
            // All the synthetic races
            ...savedSettings.MatrixConfig.SyntheticRaces.map((syntheticRace) => ({
                raceIds: syntheticRace,
                raceKey: buildRaceKeyFromRaceIds(syntheticRace),
            })),
        ];

        const getValidStationRoleColumnIdsForRaceIds = (raceIds: RaceId[]): string[] => {
            const layout = buildStationRoleColumnLayoutForRaces(raceIds, stationRoles, matrixContestants);
            return layout.map((col) => getStationRoleCellIdentifierForStationRoleAndLap(col.Roles));
        };

        // Initialize with all the ids
        races.forEach(({ raceIds, raceKey }) => {
            let allCategoryIds: CategoryId[] = [];
            let allGroupIds: GroupId[] = [];

            raceIds.forEach((raceId) => {
                allCategoryIds.push(...racesGroupsCategoriesMap.GetCategoryIDs([], raceId));
                allGroupIds.push(...racesGroupsCategoriesMap.GetGroupIDs(raceId));
            });

            allCategoryIds = arrayUnique(allCategoryIds);
            allGroupIds = arrayUnique(allGroupIds);
            const allStationRoleIds = getValidStationRoleColumnIdsForRaceIds(raceIds);

            savedSettings.CategoryMap.set(
                raceKey,
                allCategoryIds.map((id) => ({ id, enabled: false })),
            );

            savedSettings.RaceGroupMap.set(
                raceKey,
                allGroupIds.map((id) => ({ id, enabled: false })),
            );
            savedSettings.StationRolesMap.set(
                raceKey,
                allStationRoleIds.map((id) => ({ id, enabled: false })),
            );
        });

        // Enable only the ones that are enabled in the matrix settings
        matrixSettings.CategoryMap.forEach((categories, raceKey) => {
            const savedCategories = savedSettings.CategoryMap.get(raceKey);
            if (savedCategories) {
                categories.forEach((categoryId) => {
                    const category = savedCategories.find((c) => c.id === categoryId);
                    if (category) {
                        category.enabled = true;
                    }
                });
            }
        });

        matrixSettings.RaceGroupMap.forEach((groups, raceKey) => {
            const savedGroups = savedSettings.RaceGroupMap.get(raceKey);
            if (savedGroups) {
                groups.forEach((groupId) => {
                    const group = savedGroups.find((g) => g.id === groupId);
                    if (group) {
                        group.enabled = true;
                    }
                });
            }
        });

        matrixSettings.StationRolesMap.forEach((stationRoles, raceKey) => {
            const savedStationRoles = savedSettings.StationRolesMap.get(raceKey);
            if (savedStationRoles) {
                stationRoles.forEach((stationRoleId) => {
                    const stationRole = savedStationRoles.find((sr) => sr.id === stationRoleId);
                    if (stationRole) {
                        stationRole.enabled = true;
                    }
                });
            }
        });

        return savedSettings;
    }

    public constructor(val?: SavedMatrixSettingsV3) {
        this.StationRolesMap = val?.StationRolesMap ?? new Map<RaceKey, StationRoles>();
        this.RaceGroupMap = val?.RaceGroupMap ?? new Map<RaceKey, RaceGroupIDs>();
        this.CategoryMap = val?.CategoryMap ?? new Map<RaceKey, CategoryIDs>();

        this.MatrixConfig = buildDefaultMatrixConfig(val?.MatrixConfig);

        // Reset the RaceID so we dont have a preselected race
        this.MatrixConfig.RaceIDs = null;
        this.MatrixConfig.RaceKey = null;

        // Reset search so we dont have a saved search
        this.MatrixConfig.SearchString = '';
    }

    public serialize(): string {
        return JSON.stringify(this, (key, value) => {
            // Maps cant be serialized to JSON so we need to convert them to arrays
            // An array with this format can be parsed back into a map
            if (['StationRolesMap', 'RaceGroupMap', 'CategoryMap'].includes(key)) {
                return Array.from(value.entries());
            }

            return value;
        });
    }

    public deserialize(json: any): any {
        return JSON.parse(json, (key, value) => {
            // If the key is one of the maps we need to convert the array back to a map
            if (['StationRolesMap', 'RaceGroupMap', 'CategoryMap'].includes(key)) {
                return new Map(value);
            }

            return value;
        });
    }
}
